// packages/web/src/hooks/useProducts.ts
import { productService } from '@/services/product.service';
import { ProductFilters, ProductSortBy, ProductType, Product } from '@bookstore/shared';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import type { DocumentSnapshot } from 'firebase/firestore';
import { useMemo } from 'react';

/**
 * Hook to fetch featured products
 */
export const useFeaturedProducts = (productType?: ProductType, pLimit: number = 8) => {
  return useQuery({
    queryKey: ['products', 'featured', productType, pLimit],
    queryFn: () => productService.getFeaturedProducts(productType, pLimit),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to fetch products with filters and pagination
 */
export const useProducts = (
  filters?: ProductFilters,
  sortBy: ProductSortBy = 'newest',
  pageSize: number = 20
) => {
  return useInfiniteQuery<
    any,
    Error,
    any,
    (string | number | ProductFilters | undefined)[],
    DocumentSnapshot | null
  >({
    queryKey: ['products', filters, sortBy, pageSize],
    queryFn: ({ pageParam }) =>
      productService.getProducts(
        filters,
        sortBy,
        pageSize,
        (pageParam as DocumentSnapshot | null) || undefined
      ),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.lastDoc : null),
    initialPageParam: null,
    staleTime: 3 * 60 * 1000,
  });
};

/**
 * Hook to fetch a single product by ID
 */
export const useProduct = (productId: string | undefined) => {
  return useQuery({
    queryKey: ['product', productId],
    queryFn: () => productService.getProductById(productId!),
    enabled: !!productId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to fetch a product by slug
 */
export const useProductBySlug = (slug: string | undefined) => {
  return useQuery({
    queryKey: ['product', 'slug', slug],
    queryFn: () => productService.getProductBySlug(slug!),
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to fetch ALL products for client-side search
 * Cached for 30 minutes - perfect for search performance
 */
export const useAllProducts = () => {
  return useQuery({
    queryKey: ['products', 'all'],
    queryFn: () => productService.getAllProducts(),
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
};

/**
 * Client-side search with fuzzy matching
 * Much faster and more flexible than Firestore queries
 */
export const useSearchProducts = (
  searchQuery: string,
  enabled: boolean = true
) => {
  const { data: allProducts, isLoading } = useAllProducts();

  const searchResults = useMemo(() => {
    if (!allProducts || !searchQuery || searchQuery.length < 2) {
      return [];
    }

    const query = searchQuery.toLowerCase().trim();
    const queryWords = query.split(/\s+/).filter(word => word.length > 0);

    return allProducts
      .filter((product: Product) => {
        const searchText = [
          product.title,
          product.description || '',
          product.author || '',
          product.brand || '',
          product.isbn || '',
        ].join(' ').toLowerCase();

        // Match if ANY query word is a substring of the search text
        // This allows "cer" to match "CERAMIC"
        return queryWords.some(word => searchText.includes(word));
      })
      .slice(0, 20); // Limit to 20 results
  }, [allProducts, searchQuery]);

  return {
    data: enabled ? searchResults : [],
    isLoading,
  };
};

/**
 * Hook to fetch products by category
 */
export const useProductsByCategory = (
  categoryId: string | undefined,
  productType?: ProductType,
  sortBy: ProductSortBy = 'newest',
  pageSize: number = 20
) => {
  return useQuery({
    queryKey: ['products', 'category', categoryId, productType, sortBy, pageSize],
    queryFn: () => productService.getProductsByCategory(categoryId!, productType, pageSize, sortBy),
    enabled: !!categoryId,
    staleTime: 3 * 60 * 1000,
  });
};

/**
 * Hook to fetch related products
 */
export const useRelatedProducts = (
  productId: string | undefined,
  categoryId: string | undefined,
  productType?: ProductType,
  pLimit: number = 4
) => {
  return useQuery({
    queryKey: ['products', 'related', productId, categoryId, productType, pLimit],
    queryFn: () => productService.getRelatedProducts(productId!, categoryId!, productType, pLimit),
    enabled: !!productId && !!categoryId,
    staleTime: 5 * 60 * 1000,
  });
};

// ============================================
// Backward Compatibility Hooks (Books)
// ============================================

export const useFeaturedBooks = (bLimit?: number) => 
  useFeaturedProducts(ProductType.BOOK, bLimit);

export const useBooks = (filters?: ProductFilters, sortBy?: ProductSortBy, pageSize?: number) =>
  useProducts({ ...filters, productType: ProductType.BOOK }, sortBy, pageSize);

export const useBook = useProduct;
export const useSearchBooks = (searchQuery: string, enabled?: boolean) =>
  useSearchProducts(searchQuery, enabled);

export const useBooksByCategory = (
  categoryId: string | undefined,
  sortBy?: ProductSortBy,
  pageSize?: number
) => useProductsByCategory(categoryId, ProductType.BOOK, sortBy, pageSize);

export const useRelatedBooks = (
  bookId: string | undefined,
  categoryId: string | undefined,
  bLimit?: number
) => useRelatedProducts(bookId, categoryId, ProductType.BOOK, bLimit);